import type { User } from '../context/AuthContext';
import type { Profile } from '../types/database';
import { profileIsOfficial } from './profileBadgeFlags';

export function parseProfileBadges(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((b): b is string => typeof b === 'string');
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? parsed.filter((b): b is string => typeof b === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Map a Supabase profile row to the app User shape (own session or public profile view). */
export function mapProfileToUser(profile: Profile): User {
  const p = profile as Profile & {
    last_identity_sync_at?: string | null;
    stripe_connect_ready?: boolean;
  };
  const official = profileIsOfficial(p);

  return {
    id: p.id,
    username: p.username,
    displayName: p.display_name,
    email: p.email,
    bio: p.bio || '',
    avatar: p.avatar_url || undefined,
    coverUrl: p.cover_url || undefined,
    isSeller: p.is_seller,
    sellerRating: p.seller_rating ?? undefined,
    followers: p.followers_count,
    following: p.following_count,
    verified: p.verified,
    isAdmin: p.is_admin ?? false,
    isEmployee: !!(p.is_employee) && !official,
    isOfficial: official,
    stripeConnectReady: Boolean(p.stripe_connect_ready),
    isManager: Boolean(p.is_manager),
    badges: parseProfileBadges(p.badges),
    styleTags: (p.style_tags as string[]) || [],
    reputationScore: p.reputation_score || 0,
    availabilityStatus: (p.availability_status as User['availabilityStatus']) || 'offline',
    lastSeenAt: p.last_seen_at ?? undefined,
    location: p.location || '',
    website: p.website || '',
    pronouns: p.pronouns || '',
    favoriteBrands: (p.favorite_brands as string[]) || [],
    shoeSize: p.shoe_size || '',
    level: p.level || 1,
    xpPoints: p.xp_points || 0,
    cultureScore: p.culture_score || 0,
    cultureTitle: p.culture_title || 'Observer',
    styleArchetype: p.style_archetype || '',
    reputationTier: p.reputation_tier || 'Standard',
    profileTheme: p.profile_theme || 'default',
    accountType: (p.account_type as User['accountType']) || 'collector',
    activeStreak: p.active_streak || 0,
    totalSales: p.total_sales || 0,
    collectorScore: p.collector_score ?? 0,
    collectorTier: p.collector_tier ?? 'bronze',
    cultureMemberXp: p.culture_member_xp ?? 0,
    cultureMemberLevel: p.culture_member_level ?? 1,
    trendHunterPoints: p.trend_hunter_points ?? 0,
    districtPath: p.district_path ?? 'member',
    lastIdentitySyncAt: p.last_identity_sync_at || undefined,
    createdAt: p.created_at || undefined,
    myStory: p.my_story || '',
    originStory: p.origin_story || '',
    inspiration: p.inspiration || '',
    stylePhilosophy: p.style_philosophy || '',
    currentChapter: p.current_chapter || '',
    motto: p.motto || '',
    whatD88Means: p.what_d88_means || '',
    futureVision: p.future_vision || '',
    favoriteQuote: p.favorite_quote || '',
    goals: (p.goals as string[]) || [],
    dreamBrand: p.dream_brand || '',
    dreamSneaker: p.dream_sneaker || '',
    dreamGrail: p.dream_grail || '',
    currentProject: p.current_project || '',
    biggestAmbition: p.biggest_ambition || '',
    dreamCollab: p.dream_collab || '',
    outfitAesthetic: p.outfit_aesthetic || '',
    mostWornPiece: p.most_worn_piece || '',
    signatureLook: p.signature_look || '',
    favoriteColors: (p.favorite_colors as string[]) || [],
    styleEvolution: p.style_evolution || '',
    creativeInterests: (p.creative_interests as string[]) || [],
    culturalInfluences: p.cultural_influences || '',
    favoriteCity: p.favorite_city || '',
    playlistUrl: p.playlist_url || '',
    favoriteEra: p.favorite_era || '',
    communityValues: p.community_values || '',
    whatIStandFor: p.what_i_stand_for || '',
    socialLinks: (p.social_links as Record<string, string>) || {},
    openToCollab: p.open_to_collab || false,
    openToBusiness: p.open_to_business || false,
    languages: (p.languages as string[]) || [],
    networkingInterests: (p.networking_interests as string[]) || [],
    currentlyChasing: p.currently_chasing || '',
    fitOfMonth: p.fit_of_month || '',
    emailVerified: false,
    prefsPublicProfile: p.prefs_public_profile !== false,
    prefsShowVerifiedBadge: p.prefs_show_verified_badge !== false,
    prefsShowSold: p.prefs_show_sold !== false,
    prefsShowLocation: p.prefs_show_location !== false,
    prefsShowActivity: p.prefs_show_activity !== false,
    prefsDmFrom:
      p.prefs_dm_from === 'following' || p.prefs_dm_from === 'off' ? p.prefs_dm_from : 'everyone',
  };
}
